import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Merge Tailwind CSS classes with conflict resolution.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format a number as USD currency.
 */
export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a number as a percentage with sign.
 */
export function formatPercent(value: number | null | undefined): string {
  if (value === null || value === undefined) return '0.00%';
  const sign = value >= 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

/**
 * Format a number with configurable decimal places.
 */
export function formatNumber(value: number | null | undefined, decimals = 0): string {
  if (value === null || value === undefined) return '0';
  return value.toFixed(decimals);
}

/** Parse a date string, treating timezone-naive strings as UTC. */
function parseDate(date: string | Date): Date {
  if (date instanceof Date) return date;
  const hasTimezone = /[Z+]|[-]\d{2}:\d{2}$/.test(date);
  return new Date(hasTimezone ? date : `${date  }Z`);
}

export function formatDate(date: string | Date | null | undefined): string {
  if (!date) return '-';
  return parseDate(date).toLocaleString();
}

export function formatShortDate(date: string | Date): string {
  return parseDate(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Format a date for chart axis labels, adapting to data time-span.
 * - Intraday (all points same day): show time like "4:30 PM"
 * - Multi-day but < 90 days: show "Feb 17"
 * - Longer: show "Feb '26"
 */
export function formatChartLabel(date: string | Date, spanDays: number): string {
  const d = parseDate(date);
  if (spanDays < 1) {
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  }
  if (spanDays < 90) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

/**
 * Debounce a function call.
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Sleep for a given number of milliseconds.
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Capitalize the first letter of a string.
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Truncate a string to a maximum length with ellipsis.
 */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength - 3)  }...`;
}
